import { type ClassificationResult } from '#common/models/classifier.ts';

export interface AudioGenerationStep {
    name: string;
    estimatedMs: number;
}

export interface AudioGenerationDoneEvent {
    status: 'DONE';
    url: string;
    takeId: string;
    textId: string;
    elevenLabsSpeakerVoiceIdId: string;
    filePath: string;
}

export interface AudioGenerationFailedEvent {
    status: 'FAILED';
    error?: string;
    classifierResult?: ClassificationResult | { type: 'internalError'; error: string };
}

export interface AudioGenerationQueueEvent {
    status: 'IN_QUEUE';
    queuePosition?: number;
}

export interface AudioGenerationStageEvent {
    status: 'MODERATION' | 'GENERATION';
}

export interface AudioGenerationErrorEvent {
    status: 'ERROR' | 'TIMEOUT' | 'NOT_FOUND';
    error?: string;
}

export interface AudioGenerationCancelledEvent {
    status: 'CANCELLED';
}

export type AudioGenerationSseEvent =
    | AudioGenerationDoneEvent
    | AudioGenerationFailedEvent
    | AudioGenerationQueueEvent
    | AudioGenerationStageEvent
    | AudioGenerationCancelledEvent
    | AudioGenerationErrorEvent;

export interface AudioGenerationLimitReachedResponse {
    error?: string;
    limitReached: true;
    reason: 'character_limit' | 'daily_limit' | 'no_cookie_fallback' | string;
    details?: unknown;
}

export type AudioGenerationCreateResponse = { error?: string } | AudioGenerationLimitReachedResponse | {
    jobId: string;
    cancelToken: string;
    steps: AudioGenerationStep[];
};

export interface RegisterAnonymousResponse {
    success?: boolean;
    message?: string;
    registrationId?: string;
    email?: string;
    requireLogin?: boolean;
    status?: string;
    error?: Record<string, string[]> | string;
    fieldErrors?: Record<string, string[]>;
    formErrors?: string[];
}

export interface VerifyAnonymousResponse {
    success?: boolean;
    status?: string;
    error?: Record<string, string[]> | string;
    fieldErrors?: Record<string, string[]>;
    formErrors?: string[];
}

export interface FingerprintResponse {
    success?: boolean;
    anonymousId?: string;
    error?: string;
}

export async function createAudioGeneration(
    text: string,
    options: { voiceSlug?: string; speakerVoiceId?: string; voiceSettings?: unknown } = {},
): Promise<AudioGenerationCreateResponse> {
    const { voiceSlug, speakerVoiceId, voiceSettings } = options;
    const params = new URLSearchParams({ text });
    if (voiceSettings !== undefined) {
        params.set('voiceSettings', JSON.stringify(voiceSettings));
    }

    const queryParams = new URLSearchParams();
    if (voiceSlug) {
        queryParams.set('voiceSlug', voiceSlug);
    }
    if (speakerVoiceId) {
        queryParams.set('speakerVoiceId', speakerVoiceId);
    }
    const query = queryParams.size > 0 ? `?${queryParams.toString()}` : '';
    const response = await fetch(`/api/audio-generation${query}`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: params
    });

    return await response.json() as AudioGenerationCreateResponse;
}

export function openAudioGenerationStatusStream(jobId: string, cancelToken: string): EventSource {
    const params = new URLSearchParams({ jobId, cancelToken });
    return new EventSource(`/api/audio-generation/status?${params.toString()}`);
}

export async function cancelAudioGeneration(jobId: string, cancelToken: string) {
    const params = new URLSearchParams({ jobId, cancelToken });
    const response = await fetch('/api/audio-generation/cancel', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: params
    });
    if (!response.ok) {
        throw new Error('Failed to cancel audio generation.');
    }
}

export async function submitFingerprint(fingerprintId: string): Promise<FingerprintResponse> {
    const params = new URLSearchParams({ fingerprintId });
    const response = await fetch('/api/fingerprint', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: params
    });

    return await response.json() as FingerprintResponse;
}

export async function registerAnonymous(email: string, locale: string): Promise<RegisterAnonymousResponse> {
    const params = new URLSearchParams({
        email,
        marketingConsent: 'on',
        locale
    });

    const response = await fetch('/api/register-anonymous', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: params
    });

    return await response.json() as RegisterAnonymousResponse;
}

export async function verifyAnonymous(email: string, code: string): Promise<VerifyAnonymousResponse> {
    const params = new URLSearchParams({ email, code });
    const response = await fetch('/api/verify-anonymous', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
        },
        body: params
    });

    return await response.json() as VerifyAnonymousResponse;
}
